import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { AxiosResponse } from 'axios'
import { CrudState } from '../../interfaces/CrudState'
import { Prefix, PrefixType } from '../../interfaces/Prefix'
import { getXtrfProjects } from '../../../httpClient/Requests/Api/XTRF/project'
import { getXtrfQuotes } from '../../../httpClient/Requests/Api/XTRF/quote'
import { getMemsourceProjects } from '../../../httpClient/Requests/Api/MEMSOURCE/project'
import { getTempoProjects } from '../../../httpClient/Requests/Api/TEMPO/project'
import { getTempoQuotes, deleteTempoQuote } from '../../../httpClient/Requests/Api/TEMPO/quote'

export const initialState: CrudState = {
    items: [],
    loading: false,
    error: null,
}

export const fetchProjects = (prefix: PrefixType, cancelToken?: any, setCancelToken?: any) =>
    createAsyncThunk(
        `${prefix}/projects/fetchProjects`,
        async ({ filterBy }: { filterBy: any; executionCondition?: boolean }, { signal }) => {
            if (typeof cancelToken !== 'undefined') {
                cancelToken.cancel('Operation canceled due to new request.')
            }
            const source = axios.CancelToken.source()
            setCancelToken(source)
            signal.addEventListener('abort', () => {
                source.cancel()
            })
            let response: AxiosResponse<Record<string, any>[]>
            switch (prefix) {
                case Prefix.XTRF_QUOTES: {
                    response = await getXtrfQuotes(filterBy, source.token)
                    setCancelToken(undefined)
                    return response.data
                }
                case Prefix.XTRF_PROJECTS: {
                    response = await getXtrfProjects(filterBy, source.token)
                    setCancelToken(undefined)
                    return response.data
                }
                case Prefix.MEMSOURCE: {
                    response = await getMemsourceProjects(filterBy, source.token)
                    setCancelToken(undefined)
                    return response.data
                }
                case Prefix.TEMPO: {
                    response = await getTempoProjects(filterBy, source.token)
                    setCancelToken(undefined)
                    return response.data
                }
                case Prefix.TEMPO_QUOTES: {
                    response = await getTempoQuotes(filterBy, source.token)
                    setCancelToken(undefined)
                    return response.data
                }
                default:
                    return null
            }
        },
        {
            condition: ({ executionCondition }) => {
                if (!executionCondition) {
                    // Already fetched or in progress, don't need to re-fetch
                    return false
                }
            },
        },
    )

export const deleteProject = (prefix: PrefixType) =>
    createAsyncThunk(
        `${prefix}/projects/deleteProject`,
        async ({ id }: { id: string; executionCondition?: boolean }, { signal }) => {
            const source = axios.CancelToken.source()
            signal.addEventListener('abort', () => {
                source.cancel()
            })
            let response: AxiosResponse<Record<string, any>>
            switch (prefix) {
                case Prefix.TEMPO_QUOTES: {
                    response = await deleteTempoQuote({ id }, source.token)
                    return { id, response }
                }
                default:
                    return null
            }
        },
        {
            condition: ({ executionCondition = true }) => {
                if (!executionCondition) {
                    // Already fetched or in progress, don't need to re-fetch
                    return false
                }
            },
        },
    )

const projectsSlice = (prefix: PrefixType) =>
    createSlice({
        name: `${prefix}/projects`,
        initialState,
        reducers: {
            resetProjects: () => initialState,
        },
        extraReducers: (builder) => {
            // fetchProjects:
            builder.addCase(fetchProjects(prefix).pending, (state) => ({ ...state, loading: true, error: null }))
            builder.addCase(fetchProjects(prefix).fulfilled, (state, action) => ({
                ...state,
                items: action.payload,
                loading: false,
                error: null,
            }))
            builder.addCase(fetchProjects(prefix).rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.error,
            }))
            // deleteProject:
            builder.addCase(deleteProject(prefix).pending, (state) => ({ ...state, loading: true, error: null }))
            builder.addCase(deleteProject(prefix).fulfilled, (state, action) => {
                const { id } = action.payload
                const newItems = state.items.filter((item) => item.id !== id)
                return {
                    ...state,
                    items: newItems,
                    loading: false,
                    error: null,
                }
            })
            builder.addCase(deleteProject(prefix).rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.error,
            }))
        },
    })

export const projectsActions = (prefix: PrefixType) => projectsSlice(prefix).actions

const projects = (prefix: PrefixType) => projectsSlice(prefix).reducer

export default projects
